import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Calculator, Sparkle, FloppyDisk, ArrowCounterClockwise,
  CurrencyEur, Wrench, ChartLineUp,
  Warning, CheckCircle, DownloadSimple, FileText, WhatsappLogo,
  EnvelopeSimple, Printer
} from "@phosphor-icons/react";
import { Button } from "../shared/Button";
import { abrirWhatsApp } from "../../utils/api";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (val) => new Intl.NumberFormat("es-ES", { 
  style: "currency", 
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
}).format(val);

export const Formulas = ({ adminToken }) => {
  const [trabajos, setTrabajos] = useState([]);
  const [factores, setFactores] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [cambiosPendientes, setCambiosPendientes] = useState(false);
  // IDs realmente tocados — antes "Guardar" mandaba TODOS los trabajos y
  // factores cargados en memoria (90-100+ pedidos por click), y si algo se
  // había editado desde otra pantalla mientras esta estaba abierta, ese
  // cambio se pisaba en silencio con el valor viejo que traía esta pantalla.
  const [trabajosModificados, setTrabajosModificados] = useState(new Set());
  const [factoresModificados, setFactoresModificados] = useState(new Set());
  const [filtroModulo, setFiltroModulo] = useState("todos");
  const [modulos, setModulos] = useState([]);
  const [vistaActiva, setVistaActiva] = useState("trabajos"); // trabajos, factores, exportar

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [trabRes, facRes, modRes, empRes] = await Promise.all([
        fetch(`${API}/trabajos`).then(r => r.json()),
        fetch(`${API}/factores`).then(r => r.json()),
        fetch(`${API}/modulos`).then(r => r.json()),
        fetch(`${API}/empresa`).then(r => r.json()),
      ]);
      setTrabajos(trabRes);
      setFactores(facRes);
      setModulos(modRes);
      setEmpresa(empRes);
    } catch (err) {
      console.error("Error cargando datos:", err);
      alert("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const actualizarPrecioTrabajo = (trabajoId, campo, valor) => {
    setTrabajos(trabajos.map(t =>
      t.id === trabajoId ? { ...t, [campo]: parseFloat(valor) || 0 } : t
    ));
    setTrabajosModificados(prev => new Set(prev).add(trabajoId));
    setCambiosPendientes(true);
  };

  const actualizarMultiplicadorFactor = (factorId, valor) => {
    setFactores(factores.map(f =>
      f.id === factorId ? { ...f, multiplicador: parseFloat(valor) || 1 } : f
    ));
    setFactoresModificados(prev => new Set(prev).add(factorId));
    setCambiosPendientes(true);
  };

  const actualizarHorasFactor = (factorId, valor) => {
    setFactores(factores.map(f =>
      f.id === factorId ? { ...f, valor_fijo: parseFloat(valor) || 0 } : f
    ));
    setFactoresModificados(prev => new Set(prev).add(factorId));
    setCambiosPendientes(true);
  };

  const guardarCambios = async () => {
    setGuardando(true);
    try {
      // Solo lo que realmente se tocó — no todo lo que está cargado en
      // memoria, así no se pisa ni un solo campo que no se editó acá.
      const trabajosAGuardar = trabajos.filter(t => trabajosModificados.has(t.id));
      const factoresAGuardar = factores.filter(f => factoresModificados.has(f.id));

      await Promise.all([
        ...trabajosAGuardar.map(t =>
          fetch(`${API}/admin/trabajos/${t.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${adminToken}`
            },
            body: JSON.stringify(t)
          })
        ),
        ...factoresAGuardar.map(f =>
          fetch(`${API}/admin/factores/${f.id}`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${adminToken}`
            },
            body: JSON.stringify(f)
          })
        ),
      ]);

      alert(`✅ Guardado: ${trabajosAGuardar.length} trabajo(s), ${factoresAGuardar.length} factor(es)`);
      setCambiosPendientes(false);
      setTrabajosModificados(new Set());
      setFactoresModificados(new Set());
    } catch (err) {
      console.error("Error guardando:", err);
      alert("❌ Error al guardar cambios");
    } finally {
      setGuardando(false);
    }
  };

  const resetearCambios = () => {
    if (window.confirm("¿Descartar todos los cambios sin guardar?")) {
      cargarDatos();
      setCambiosPendientes(false);
      setTrabajosModificados(new Set());
      setFactoresModificados(new Set());
    }
  };

  const generarPDFListaPrecios = () => {
    const doc = new jsPDF();
    
    // Header con logo/empresa
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(26, 58, 74); // Brand primary
    doc.text(empresa?.nombre || "AAGG Reformas", 105, 20, { align: "center" });
    
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(empresa?.slogan || "Tu reforma, hecha bien. Sin sorpresas.", 105, 28, { align: "center" });
    
    // Info empresa
    doc.setFontSize(9);
    doc.text(`📞 ${empresa?.telefono || ""}`, 20, 40);
    doc.text(`📧 ${empresa?.email || ""}`, 20, 46);
    doc.text(`📍 ${empresa?.direccion || ""}`, 20, 52);
    
    // Fecha
    doc.text(`Fecha: ${new Date().toLocaleDateString("es-ES")}`, 160, 40);
    
    // Título
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(201, 162, 39); // Accent
    doc.text("LISTA DE PRECIOS", 105, 65, { align: "center" });
    
    // Agrupar por módulo
    let currentY = 80;
    modulos.forEach(modulo => {
      const trabajosModulo = trabajos.filter(t => t.modulo === modulo.id);
      if (trabajosModulo.length === 0) return;
      
      // Header módulo
      doc.setFillColor(26, 58, 74);
      doc.rect(20, currentY - 5, 170, 8, "F");
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(`${modulo.icono} ${modulo.nombre.toUpperCase()}`, 22, currentY);
      currentY += 12;
      
      // Tabla de trabajos
      const tableData = trabajosModulo.map(t => [
        t.nombre,
        t.unidad,
        `${t.precio_eco}€`,
        `${t.precio_std}€`,
        `${t.precio_prem}€`
      ]);
      
      doc.autoTable({
        startY: currentY,
        head: [["Trabajo", "Unidad", "ECO 💚", "STD 🔵", "PREMIUM 🟠"]],
        body: tableData,
        theme: "striped",
        headStyles: { 
          fillColor: [241, 245, 249],
          textColor: [26, 58, 74],
          fontStyle: "bold",
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [60, 60, 60]
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: 30, halign: "center" },
          2: { cellWidth: 20, halign: "right", textColor: [34, 197, 94] },
          3: { cellWidth: 20, halign: "right", textColor: [59, 130, 246] },
          4: { cellWidth: 20, halign: "right", textColor: [249, 115, 22] }
        },
        margin: { left: 20, right: 20 }
      });
      
      currentY = doc.lastAutoTable.finalY + 10;
      
      // Nueva página si es necesario
      if (currentY > 250) {
        doc.addPage();
        currentY = 20;
      }
    });
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Página ${i} de ${pageCount} · ${empresa?.nombre || "AAGG Reformas"} · Precios válidos hasta fin de mes`,
        105,
        285,
        { align: "center" }
      );
    }
    
    // Disclaimer
    doc.setPage(pageCount);
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    const disclaimerY = Math.min(currentY + 10, 270);
    doc.text(
      "* Precios orientativos. Presupuesto final sujeto a inspección técnica.",
      105,
      disclaimerY,
      { align: "center" }
    );
    doc.text(
      "* IVA no incluido. Materiales calculados según calidad seleccionada.",
      105,
      disclaimerY + 4,
      { align: "center" }
    );
    
    return doc;
  };

  const descargarPDF = () => {
    const doc = generarPDFListaPrecios();
    doc.save(`Lista_Precios_${empresa?.nombre?.replace(/\s/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  const enviarPorWhatsApp = () => {
    const mensaje = `🏠 *${empresa?.nombre || "AAGG REFORMAS"}*\n\n` +
      `📋 *LISTA DE PRECIOS*\n\n` +
      `Hola! Te envío nuestra lista de precios actualizada.\n\n` +
      `Algunos ejemplos:\n` +
      trabajos.slice(0, 5).map(t =>
        `• ${t.nombre}: desde ${t.precio_eco}€/${t.unidad}\n`
      ).join("") +
      `\n💡 Estos son precios orientativos.\n` +
      `Te puedo preparar un presupuesto detallado sin compromiso.\n\n` +
      `📞 ${empresa?.telefono || ""}\n` +
      `📧 ${empresa?.email || ""}`;

    const telefono = prompt("Introduce el teléfono del cliente:");
    if (telefono) {
      abrirWhatsApp(telefono, mensaje);
    }
  };

  const enviarPorEmail = () => {
    const asunto = encodeURIComponent(`Lista de Precios - ${empresa?.nombre || "AAGG Reformas"}`);
    const cuerpo = encodeURIComponent(
      `Hola,\n\nAdjunto encontrarás nuestra lista de precios actualizada.\n\n` +
      `Precios orientativos. Puedo prepararte un presupuesto detallado sin compromiso.\n\n` +
      `Saludos,\n${empresa?.nombre || "AAGG Reformas"}\n` +
      `${empresa?.telefono || ""}\n${empresa?.email || ""}`
    );
    
    window.open(`mailto:?subject=${asunto}&body=${cuerpo}`, "_blank");
    alert("📧 Se ha abierto tu cliente de email.\n\n💡 Adjunta manualmente el PDF descargado.");
  };

  const imprimirListaPrecios = () => {
    const doc = generarPDFListaPrecios();
    const pdfDataUri = doc.output("dataurlstring");
    const printWindow = window.open(pdfDataUri);
    printWindow.print();
  };

  const trabajosFiltrados = filtroModulo === "todos" 
    ? trabajos 
    : trabajos.filter(t => t.modulo === filtroModulo);

  const conteoTotal = {
    trabajos: trabajos.length,
    factores: factores.length,
    precios_totales: trabajos.length * 3, // eco + std + prem
    suma_precios_eco: trabajos.reduce((sum, t) => sum + t.precio_eco, 0),
    suma_precios_std: trabajos.reduce((sum, t) => sum + t.precio_std, 0),
    suma_precios_prem: trabajos.reduce((sum, t) => sum + t.precio_prem, 0),
    precio_medio_eco: trabajos.reduce((sum, t) => sum + t.precio_eco, 0) / trabajos.length || 0,
    precio_medio_std: trabajos.reduce((sum, t) => sum + t.precio_std, 0) / trabajos.length || 0,
    precio_medio_prem: trabajos.reduce((sum, t) => sum + t.precio_prem, 0) / trabajos.length || 0,
  };

  if (loading) {
    return (
      <div className="container pt-4">
        <div className="text-center py-12">⏳ Cargando fórmulas...</div>
      </div>
    );
  }

  return (
    <div className="container pt-4 pb-24">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-h2 font-display flex items-center gap-2">
            <Calculator size={28} weight="fill" style={{ color: "var(--brand-accent)" }} />
            Fórmulas y Precios
          </h1>
          <p className="text-small text-secondary">
            Control total de precios, porcentajes y multiplicadores
          </p>
        </div>
        {cambiosPendientes && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetearCambios}>
              <ArrowCounterClockwise size={18} /> Descartar
            </Button>
            <Button variant="success" size="sm" onClick={guardarCambios} disabled={guardando}>
              <FloppyDisk size={18} /> {guardando ? "Guardando..." : "Guardar cambios"}
            </Button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-hide">
        <button
          onClick={() => setVistaActiva("trabajos")}
          className={`chip chip-interactive ${vistaActiva === "trabajos" ? "chip-active" : ""}`}
        >
          <Wrench size={16} /> Precios Trabajos
        </button>
        <button
          onClick={() => setVistaActiva("factores")}
          className={`chip chip-interactive ${vistaActiva === "factores" ? "chip-active" : ""}`}
        >
          <Sparkle size={16} /> Factores
        </button>
        <button
          onClick={() => setVistaActiva("exportar")}
          className={`chip chip-interactive ${vistaActiva === "exportar" ? "chip-active" : ""}`}
        >
          <FileText size={16} /> Exportar Lista Precios
        </button>
      </div>

      {/* Conteo Total */}
      <div className="card p-4 mb-4" style={{ background: "linear-gradient(135deg, #1A3A4A 0%, #2C5F74 100%)", color: "white" }}>
        <div className="flex items-center gap-2 mb-3">
          <ChartLineUp size={24} weight="fill" style={{ color: "var(--brand-accent)" }} />
          <div className="text-h3 font-display">Resumen Global</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>Total Trabajos</div>
            <div className="text-h3 font-bold">{conteoTotal.trabajos}</div>
          </div>
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>Total Factores</div>
            <div className="text-h3 font-bold">{conteoTotal.factores}</div>
          </div>
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>Precios Configurados</div>
            <div className="text-h3 font-bold">{conteoTotal.precios_totales}</div>
          </div>
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>Precio Medio STD</div>
            <div className="text-h3 font-bold">{formatCurrency(conteoTotal.precio_medio_std)}</div>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/20">
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>💚 ECO Total</div>
            <div className="text-body font-bold" style={{ color: "#4ade80" }}>
              {formatCurrency(conteoTotal.suma_precios_eco)}
            </div>
          </div>
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>🔵 STD Total</div>
            <div className="text-body font-bold" style={{ color: "#3b82f6" }}>
              {formatCurrency(conteoTotal.suma_precios_std)}
            </div>
          </div>
          <div>
            <div className="text-tiny" style={{ opacity: 0.7 }}>🟠 PREMIUM Total</div>
            <div className="text-body font-bold" style={{ color: "#f97316" }}>
              {formatCurrency(conteoTotal.suma_precios_prem)}
            </div>
          </div>
        </div>
      </div>

      {/* VISTA: Precios Trabajos */}
      {vistaActiva === "trabajos" && (
        <div>
          {/* Filtro módulos */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
            <button
              onClick={() => setFiltroModulo("todos")}
              className={`chip chip-interactive ${filtroModulo === "todos" ? "chip-active" : ""}`}
            >
              Todos ({trabajos.length})
            </button>
            {modulos.map(mod => (
              <button
                key={mod.id}
                onClick={() => setFiltroModulo(mod.id)}
                className={`chip chip-interactive ${filtroModulo === mod.id ? "chip-active" : ""}`}
              >
                {mod.icono} {mod.nombre} ({trabajos.filter(t => t.modulo === mod.id).length})
              </button>
            ))}
          </div>

          {/* Tabla editable */}
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-subtle">
                  <th className="text-left p-3 text-small font-bold">Trabajo</th>
                  <th className="text-center p-3 text-small font-bold">Unidad</th>
                  <th className="text-right p-3 text-small font-bold" style={{ color: "#22c55e" }}>💚 ECO</th>
                  <th className="text-right p-3 text-small font-bold" style={{ color: "#3b82f6" }}>🔵 STD</th>
                  <th className="text-right p-3 text-small font-bold" style={{ color: "#f97316" }}>🟠 PREMIUM</th>
                </tr>
              </thead>
              <tbody>
                {trabajosFiltrados.map(trabajo => (
                  <tr key={trabajo.id} className="border-b border-subtle hover:bg-gray-50">
                    <td className="p-3 text-small">{trabajo.nombre}</td>
                    <td className="p-3 text-center text-tiny text-secondary">{trabajo.unidad}</td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={trabajo.precio_eco}
                        onChange={(e) => actualizarPrecioTrabajo(trabajo.id, "precio_eco", e.target.value)}
                        className="input input-sm text-right"
                        style={{ width: "72px", minWidth: "72px", fontSize: "0.875rem" }}
                        step="0.5"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={trabajo.precio_std}
                        onChange={(e) => actualizarPrecioTrabajo(trabajo.id, "precio_std", e.target.value)}
                        className="input input-sm text-right"
                        style={{ width: "72px", minWidth: "72px", fontSize: "0.875rem" }}
                        step="0.5"
                      />
                    </td>
                    <td className="p-2">
                      <input
                        type="number"
                        value={trabajo.precio_prem}
                        onChange={(e) => actualizarPrecioTrabajo(trabajo.id, "precio_prem", e.target.value)}
                        className="input input-sm text-right"
                        style={{ width: "72px", minWidth: "72px", fontSize: "0.875rem" }}
                        step="0.5"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VISTA: Factores */}
      {vistaActiva === "factores" && (
        <div className="space-y-3">
          {factores.map(factor => (
            <div key={factor.id} className="card p-4">
              <div className="flex items-center gap-3">
                <div style={{ fontSize: "1.5rem" }}>{factor.icono}</div>
                <div className="flex-1">
                  <div className="font-bold text-body">{factor.nombre}</div>
                  <div className="text-tiny text-secondary">{factor.descripcion}</div>
                  <div className="text-tiny" style={{ color: "var(--warning)" }}>{factor.categoria}</div>
                </div>
                {factor.tipo === "fijo_hora" ? (
                  <div className="flex items-center gap-2">
                    <span className="text-small text-secondary">Horas extra:</span>
                    <input
                      type="number"
                      value={factor.valor_fijo}
                      onChange={(e) => actualizarHorasFactor(factor.id, e.target.value)}
                      className="input input-sm text-center"
                      style={{ maxWidth: "80px" }}
                      step="0.5"
                      min="0"
                      max="12"
                    />
                    <span className={`text-small font-bold ${factor.valor_fijo > 0 ? "text-error" : "text-success"}`}>
                      {factor.valor_fijo > 0 ? "+" : ""}{factor.valor_fijo}h
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="text-small text-secondary">Multiplicador:</span>
                    <input
                      type="number"
                      value={factor.multiplicador}
                      onChange={(e) => actualizarMultiplicadorFactor(factor.id, e.target.value)}
                      className="input input-sm text-center"
                      style={{ maxWidth: "80px" }}
                      step="0.05"
                      min="0.1"
                      max="3"
                    />
                    <span className={`text-small font-bold ${factor.multiplicador > 1 ? "text-error" : "text-success"}`}>
                      {factor.multiplicador > 1 ? "+" : ""}{((factor.multiplicador - 1) * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* VISTA: Exportar Lista Precios */}
      {vistaActiva === "exportar" && (
        <div className="space-y-4">
          <div className="card card-dark p-5 text-center">
            <FileText size={56} weight="fill" style={{ margin: "0 auto 12px", color: "var(--brand-accent)" }} />
            <div className="text-h3 font-display mb-2">Lista de Precios Profesional</div>
            <p className="text-small" style={{ opacity: 0.8 }}>
              Genera un PDF profesional con todos tus precios, datos de empresa y disclaimer legal.
              Perfecto para enviar a clientes potenciales.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button variant="primary" className="btn-full" onClick={descargarPDF}>
              <DownloadSimple size={20} /> Descargar PDF
            </Button>
            <Button variant="outline" className="btn-full" onClick={imprimirListaPrecios}>
              <Printer size={20} /> Imprimir
            </Button>
            <Button variant="whatsapp" className="btn-full" onClick={enviarPorWhatsApp}>
              <WhatsappLogo size={20} weight="fill" /> Enviar por WhatsApp
            </Button>
            <Button variant="outline" className="btn-full" onClick={enviarPorEmail}>
              <EnvelopeSimple size={20} /> Enviar por Email
            </Button>
          </div>

          <div className="card p-4" style={{ background: "#FFF8E1", border: "1px solid #F59E0B" }}>
            <div className="flex items-start gap-3">
              <Warning size={24} style={{ color: "#F59E0B", flexShrink: 0 }} />
              <div>
                <div className="font-bold text-small mb-1" style={{ color: "#92400E" }}>
                  💡 Consejos para enviar la lista de precios
                </div>
                <ul className="text-tiny text-secondary space-y-1">
                  <li>• <strong>WhatsApp:</strong> Se enviará un resumen con los 5 primeros trabajos + enlace a PDF completo</li>
                  <li>• <strong>Email:</strong> Adjunta manualmente el PDF después de descargarlo</li>
                  <li>• <strong>Imprimir:</strong> Ideal para entregas presenciales o archivo físico</li>
                  <li>• La lista incluye disclaimer legal: "Precios orientativos sujetos a inspección"</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Preview muestra */}
          <div className="card p-4">
            <div className="text-label mb-3">👁️ Vista previa del contenido</div>
            <div className="text-tiny text-secondary space-y-1">
              <div>✅ Header con logo y datos de empresa</div>
              <div>✅ Trabajos agrupados por módulo ({modulos.length} categorías)</div>
              <div>✅ {trabajos.length} trabajos con 3 niveles de precio (ECO/STD/PREMIUM)</div>
              <div>✅ Footer con disclaimer y validez</div>
              <div>✅ Paginación automática si hay muchos trabajos</div>
            </div>
          </div>
        </div>
      )}

      {/* Warning si hay cambios sin guardar */}
      {cambiosPendientes && (
        <div 
          className="fixed bottom-20 left-1/2 transform -translate-x-1/2 card p-4 shadow-lg"
          style={{ background: "#FEF3C7", border: "2px solid #F59E0B", zIndex: 999 }}
        >
          <div className="flex items-center gap-3">
            <Warning size={24} style={{ color: "#F59E0B" }} />
            <div>
              <div className="font-bold text-small" style={{ color: "#92400E" }}>
                Tienes cambios sin guardar
              </div>
              <div className="text-tiny text-secondary">
                Recuerda guardar antes de salir
              </div>
            </div>
            <Button variant="success" size="sm" onClick={guardarCambios} disabled={guardando}>
              <FloppyDisk size={16} /> Guardar ahora
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
